import React from "react";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { Warning, Visibility, DeleteOutline } from "@material-ui/icons";
import humanFileSize from "../../../tools/humanFileSize";
import {
  IMPORT_ISSUE_PALETTE,
  getFileIssueColor,
} from "./importIssuePalette";

export const FILE_LIST_ROW_HEIGHT = 40;

export const FileListOuterElement = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function FileListOuterElement({ style, className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      {...rest}
      className={className}
      style={{
        ...style,
        transition: "none",
        animation: "none",
      }}
    />
  );
});

type FileListEntry = {
  uid: number;
  file: File;
  copyIndex?: number;
  copyTotal?: number;
};

export type ImportFileListItemData = {
  entries: FileListEntry[];
  bigSet: Set<string>;
  duplicateSet: Set<string>;
  diffDimSet: Set<string>;
  activeFilter: string | null;
  onRemove: (uid: number) => void;
  onPreview: (file: File) => void;
  filePreviewButtonClass?: string;
};

type ImportFileListRowProps = {
  index: number;
  style: React.CSSProperties;
  data: ImportFileListItemData;
};

const getFileIssues = (
  name: string,
  bigSet: Set<string>,
  duplicateSet: Set<string>,
  diffDimSet: Set<string>,
) => {
  const issues: string[] = [];
  if (bigSet.has(name)) issues.push("Trop volumineux");
  if (duplicateSet.has(name)) issues.push("Doublon");
  if (diffDimSet.has(name)) issues.push("Dimensions différentes");
  return issues;
};

const ImportFileListRow = ({ index, style, data }: ImportFileListRowProps) => {
  const {
    entries,
    bigSet,
    duplicateSet,
    diffDimSet,
    activeFilter,
    onRemove,
    onPreview,
    filePreviewButtonClass,
  } = data;
  const { uid, file, copyIndex, copyTotal } = entries[index];
  const issues = getFileIssues(file.name, bigSet, duplicateSet, diffDimSet);
  const issueColor = getFileIssueColor(
    file.name,
    bigSet,
    duplicateSet,
    diffDimSet,
  );
  const palette = issueColor ? IMPORT_ISSUE_PALETTE[issueColor] : null;
  const isDuplicateGroupView = activeFilter === "duplicates";
  const prevEntry = index > 0 ? entries[index - 1] : null;
  const nextEntry = index < entries.length - 1 ? entries[index + 1] : null;
  const isGroupStart =
    isDuplicateGroupView &&
    (!prevEntry || prevEntry.file.name !== file.name);
  const isGroupEnd =
    isDuplicateGroupView &&
    (!nextEntry || nextEntry.file.name !== file.name);
  const duplicatePalette = IMPORT_ISSUE_PALETTE.warning;

  return (
    <div
      style={{
        ...style,
        boxSizing: "border-box",
        paddingTop: isGroupStart && index > 0 ? 6 : 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          paddingLeft: palette || isDuplicateGroupView ? 14 : 10,          
          borderBottom: "1px solid #f3f3f3",
          borderLeft: palette ? `3px solid ${palette.accent}` : undefined,
          backgroundColor: palette?.background,
          height: FILE_LIST_ROW_HEIGHT,
          boxSizing: "border-box",
        }}
      >
        {issues.length > 0 ? (
          <Tooltip title={issues.join(" · ")}>
            <Warning
              style={{
                fontSize: 18,
                color: palette?.iconColor,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        ) : (
          <span style={{ width: 18, flexShrink: 0 }} />
        )}
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={file.name}
        >
          {file.name}
        </span>
        {isDuplicateGroupView && copyTotal != null && copyTotal > 1 && (
          <span
            style={{
              color: duplicatePalette.color,
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
              backgroundColor: duplicatePalette.background,
              padding: "1px 6px",
              borderRadius: 4,
            }}
          >
            {copyIndex}/{copyTotal}
          </span>
        )}
        {issues.length > 0 && !isDuplicateGroupView && (
          <span
            style={{
              color: palette?.color,
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {issues.join(", ")}
          </span>
        )}
        <span style={{ color: "#888", flexShrink: 0 }}>
          {humanFileSize(file.size)}
        </span>
        <Tooltip title="Aperçu">
          <IconButton
            className={filePreviewButtonClass}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPreview(file);
            }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        <DeleteOutline
          style={{
            cursor: "pointer",
            width: "20px",
            height: "20px",
            flexShrink: 0,
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(uid);
          }}
        />
      </div>
    </div>
  );
};

export default React.memo(ImportFileListRow);
